import React, { useState } from 'react';
import { X, Check, Award } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import { getPetName, getPreviewImage } from '../lib/petUtils';
import { usePetStore } from '../store/usePetStore';
import CosImage from './CosImage';

// Re-export getPetName for backward compatibility (though imports should be updated eventually)
export { getPetName } from '../lib/petUtils';
// getPetImage is no longer exported from here as its signature changed and it moved

interface PetSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  studentName: string;
  studentId: number;
  unlockedPets?: string[];
  onSelect: () => void;
}

export default function PetSelectionModal({
  isOpen,
  onClose,
  studentName,
  studentId,
  unlockedPets = [],
  onSelect,
}: PetSelectionModalProps) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [showMaxLevel, setShowMaxLevel] = useState(false);
  const petConfig = usePetStore((state) => state.petConfig);

  const handleSelectPet = async (petId: string) => {
    try {
      setLoading(true);
      await api.put(`/students/${studentId}`, { petType: petId });
      onSelect();
      onClose();
    } catch (error) {
      console.error('Failed to select pet', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 sm:p-6">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      {/* Modal Content */}
      <div className="relative bg-white rounded-[2rem] shadow-2xl w-full max-w-6xl max-h-[85vh] overflow-hidden animate-in fade-in zoom-in-95 duration-300 flex flex-col border border-white/20">
        {/* Header */}
        <div className="relative overflow-hidden bg-indigo-600 text-white p-5 shrink-0">
          <div className="relative z-10 flex flex-row items-center justify-between gap-4">
            <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-center sm:gap-4">
              <h2 className="text-xl sm:text-2xl font-black tracking-tight drop-shadow-sm whitespace-nowrap">
                {t('petSelection.title')}
              </h2>
              <div className="hidden sm:block w-px h-6 bg-indigo-400/50"></div>
              <p className="text-indigo-100 text-sm sm:text-base font-medium leading-relaxed opacity-90 truncate">
                {t('petSelection.subtitle', { studentName })}
              </p>
            </div>
            
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => setShowMaxLevel(!showMaxLevel)}
                className="group relative px-3 py-1.5 bg-white/20 hover:bg-white/30 backdrop-blur-md text-white rounded-lg transition-all duration-300 border border-white/30 hover:border-white/50 shadow-md hover:shadow-indigo-500/30 overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></div>
                <div className="flex items-center gap-1.5 font-bold tracking-wide text-xs">
                  {showMaxLevel ? (
                     <>
                       <span className="text-base">🐣</span>
                       <span>{t('petSelection.viewBaby')}</span>
                     </>
                  ) : (
                    <>
                      <span className="text-base">👑</span>
                      <span>{t('petSelection.viewMax')}</span>
                    </>
                  )}
                </div>
              </button>
              
              <button 
                onClick={onClose}
                className="p-1.5 bg-white/10 hover:bg-white/25 text-white rounded-full transition-all duration-200 backdrop-blur-sm border border-white/10 hover:rotate-90"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6 sm:p-10 custom-scrollbar bg-slate-50/50">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6 sm:gap-8">
            {petConfig.map((config) => {
              const isUnlocked = unlockedPets?.includes(config.id);
              const petName = getPetName(config.id);
              return (
                <button
                  key={config.id}
                  onClick={() => handleSelectPet(config.id)}
                  disabled={loading}
                  className="group relative bg-white rounded-3xl p-6 border border-slate-100 shadow-sm hover:shadow-2xl hover:shadow-indigo-500/10 hover:border-indigo-200 transition-all duration-300 flex flex-col items-center justify-between gap-4 hover:-translate-y-2 overflow-hidden"
                >
                  {/* Hover gradient background */}
                  <div className="absolute inset-0 bg-gradient-to-br from-indigo-50/50 to-purple-50/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                  {isUnlocked && (
                    <div className="absolute top-0 right-0 z-20 bg-gradient-to-bl from-amber-400 to-orange-500 text-white text-[10px] font-bold px-3 py-1.5 rounded-bl-2xl shadow-sm flex items-center gap-1">
                      <Award className="w-3 h-3" /> {t('petSelection.unlocked')}
                    </div>
                  )}
                  
                  <div className="relative w-28 h-28 sm:w-32 sm:h-32 transition-transform duration-500 group-hover:scale-110 drop-shadow-md group-hover:drop-shadow-xl">
                    <CosImage 
                      src={getPreviewImage(config, showMaxLevel)} 
                      alt={petName} 
                      className={`w-full h-full object-contain filter transition-all duration-300 ${isUnlocked ? 'brightness-110 saturate-110' : ''}`}
                    />
                  </div>
                  
                  <div className="relative z-10 flex flex-col items-center gap-1">
                    <span className="font-black text-slate-700 text-lg sm:text-xl tracking-tight group-hover:text-indigo-600 transition-colors">
                      {petName}
                    </span>
                    <div className="h-1 w-0 group-hover:w-full bg-indigo-500 rounded-full transition-all duration-300"></div>
                  </div>
                  
                  {/* Selection Indicator Overlay */}
                  <div className="absolute inset-0 bg-indigo-600/10 opacity-0 group-active:opacity-100 transition-opacity duration-100 pointer-events-none"></div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Footer Hint */}
        <div className="p-5 border-t border-slate-100 bg-white/80 backdrop-blur-xl text-center">
          <p className="text-slate-400 text-sm font-medium flex items-center justify-center gap-2">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-indigo-400 animate-pulse"></span>
            {t('petSelection.hint')}
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-indigo-400 animate-pulse"></span>
          </p>
        </div>
      </div>
    </div>
  );
}
